<template>
  <div class="care-templates-container">
    <LoadingIndicator
      v-if="loading"
      :text="currentLang === 'zh' ? '加载中...' : 'Loading...'"
      min-height="400px"
    />

    <div v-else-if="error" class="error-container">
      <p class="error-text">{{ error }}</p>
    </div>

    <div v-else-if="displayTemplates.length === 0" class="empty-state">
      <img :src="imgListBlank" class="empty-state-img" alt="" />
      <p>No template found</p>
    </div>

    <div v-else class="templates-grid">
      <div
        v-for="template in displayTemplates"
        :key="template.id"
        class="template-card"
        :class="{ active: selectedTemplate === String(template.id) }"
        @click="handleCardClick(template)"
      >
        <div class="card-header">
          <div class="card-title-section">
            <h3 class="card-title" v-html="formatTitle(template.template_name)"></h3>
            <div class="card-actions">
              <button class="action-btn" @click.stop="handleApply(template)">
                <img :src="applyToIcon" alt="apply to" class="action-icon" />
              </button>
              <button class="action-btn" @click.stop="handleEdit(template)">
                <img :src="pceditIcon" alt="edit" class="action-icon" />
              </button>
            </div>
          </div>
          <div class="card-duration">{{ getDurationText(template) }}</div>
        </div>

        <div class="card-body">
          <div v-if="template.items && template.items.length > 0" class="checks-list">
            <div v-for="item in template.items" :key="item.id" class="check-item">
              <span class="check-id">{{ item.item_id }}</span>
              <span class="check-name" v-html="formatCheckName(item.item_name)"></span>
            </div>
          </div>
          <div v-else class="no-checks">No checks</div>
        </div>
      </div>
    </div>

    <EditEE
      :visible="showEditModal"
      :template-id="editingTemplateId"
      :plan-key="'essential_care'"
      @close="handleCloseEditModal"
      @saved="handleSaved"
    />

    <ApplyEE
      :visible="showApplyModal"
      :template-id="applyingTemplateId"
      :plan-key="'essential_care'"
      @close="handleCloseApplyModal"
      @saved="handleSaved"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import '@/styles/care-card-title.css'
import { getCareTemplateList, type CareTemplate } from '@/api/careTemplate'
import { getCareTemplateDetail, type CareTemplateDetailResponse } from '@/api/caredetail'
import applyToIcon from '@/assets/apply to.png'
import pceditIcon from '@/assets/pcedit.png'
import LoadingIndicator from '@/components/LoadingIndicator.vue'
import imgListBlank from '@/assets/list_blank@2x.png'
import EditEE from './EditEE.vue'
import ApplyEE from './ApplyEE.vue'

const selectedTemplate = ref<string | null>(null)
const loading = ref(false)
const error = ref<string>('')
const currentLang = ref<'en' | 'zh'>('en')

const templates = ref<CareTemplate[]>([])
const editingTemplateId = ref<number | null>(null)
const showEditModal = ref(false)
const applyingTemplateId = ref<number | null>(null)
const showApplyModal = ref(false)

const displayTemplates = computed<CareTemplate[]>(() => {
  return templates.value
})

function getDurationText(template: CareTemplate): string {
  if (template.estimated_hours > 0) {
    return `${template.estimated_hours} hours`
  }
  if (template.estimated_minutes > 0) {
    return `${template.estimated_minutes} mins`
  }
  return ''
}

function formatTitle(title: string): string {
  return title.replace('Visit', '<br/>Visit')
}

function formatCheckName(name: string): string {
  const match = name.match(/^(.+?)\s*(\(.*\))$/)
  if (match && match[1] && match[2]) {
    return `${match[1].trim()}<br/>${match[2]}`
  }
  return name
}

function handleCardClick(template: CareTemplate): void {
  selectedTemplate.value = String(template.id)
}

function handleApply(template: CareTemplate): void {
  applyingTemplateId.value = template.id
  showApplyModal.value = true
}

async function handleEdit(template: CareTemplate): Promise<void> {
  try {
    const response: CareTemplateDetailResponse = await getCareTemplateDetail({
      id: template.id,
    })

    if (response.code === 200 && response.data) {
      editingTemplateId.value = template.id
      showEditModal.value = true
    }
  } catch {
    // 静默处理错误
  }
}

function handleCloseEditModal(): void {
  showEditModal.value = false
  editingTemplateId.value = null
}

function handleCloseApplyModal(): void {
  showApplyModal.value = false
  applyingTemplateId.value = null
}

function handleSaved(): void {
  void fetchTemplates()
}

async function fetchTemplates(): Promise<void> {
  loading.value = true
  error.value = ''

  try {
    const response = await getCareTemplateList({
      plan_key: 'essential_care',
    })

    if (response.code === 200 && response.data && response.data.list) {
      templates.value = response.data.list
    } else {
      error.value = response.msg || 'Failed to load templates'
    }
  } catch {
    error.value = 'Network error, please try again later'
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  void fetchTemplates()
})
</script>

<style scoped>
/* ========== 完全复制Premium页面的容器样式 ========== */
.care-templates-container,
.premium-care-container {
  width: 100%;
  padding: 0;
  box-sizing: border-box;
}

.placeholder-content p {
  color: #8a8a8a;
  font-size: 16px;
}

.error-container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
}

.error-text {
  color: #ff4d4f;
  font-size: 14px;
}
.templates-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, 274px);
  gap: 24px;
  padding: 0 0 24px;
  margin: 15px 0 0 -9px;
  width: 100%;
  box-sizing: border-box;
  justify-content: flex-start;
  max-height: none;
  overflow-y: visible;
  overflow-x: visible;
}

.template-card {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 0;
  cursor: pointer;
  transition: all 0.2s;
  height: 480px;
  width: 100%;
  max-width: 274px;
  margin: 0;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;
}

.template-card.active {
  border-color: #666666;
  box-shadow: none;
}

.card-header {
  padding: 16px 16px 16px;
  position: sticky;
  top: 0;
  background: #ffffff;
  z-index: 20;
}

.card-title-section {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  margin-bottom: 16px;
}

.card-title {
  flex: 1;
}

.card-actions {
  display: flex;
  gap: 16px;
  flex-shrink: 0;
  position: absolute;
  top: 18px;
  right: 16px;
}

.action-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.action-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.card-duration {
  font-family:
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #000000;
  text-align: left;
  margin: 0;
}

.card-body {
  flex: 1;
  padding: 0 0 16px 16px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.card-body::-webkit-scrollbar {
  width: 6px;
}

.card-body::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.card-body::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.card-body::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}

.checks-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-right: 16px;
}

.check-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.check-id {
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #999999;
}

.check-name {
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #000000;
  word-break: break-word;
  white-space: normal;
  word-wrap: break-word;
}

.check-name :deep(br) {
  display: block;
  content: '';
  margin: 4px 0;
}

.no-checks {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ccc;
  font-size: 13px;
}

@media (max-width: 320px) {
  .template-card {
    height: auto;
    min-height: 480px;
  }
}
</style>
